import React, { FC, useEffect, useState } from 'react';
import { DatePickerProps, message, Row, Spin } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import moment, { Moment } from 'moment';
import { useNavigate } from 'react-router-dom';
import { api, endpoints } from '../../api';
import { PdfViewerComponent } from '../../components/PdfViewerComponent/PdfViewerComponent';
import {
  BlueLayout,
  CustomButton,
  CustomCheckbox,
  CustomDatePicker,
  CustomLayout,
  FormBlock,
  GridItem,
  PagesContainerUI,
} from '../../styles/styled';
import { sortPublishers } from '../../utils/sortObject';
import { PublishersType, SchoolClassesType } from '../NewApplicationPage/types';
import NmsMeetingMaterial from './components/NmsMeetingMaterial';
import { NmsMeetingType } from './types';

type CreateNmsMeetingPageProps = {
  editNmsMeeting?: boolean;
  currentNmsMeeting?: NmsMeetingType | null;
  id?: number | string;
};

export const CreateNmsMeetingPage: FC<CreateNmsMeetingPageProps> = ({ editNmsMeeting, currentNmsMeeting, id }) => {
  const [nmsMeetingState, setNmsMeeting] = useState<NmsMeetingType>({});
  const [publishersState, setPublishers] = useState<PublishersType>([]);
  const [schoolClassesState, setSchoolClasses] = useState<SchoolClassesType>([]);
  const [changesState, setChangesState] = useState<NmsMeetingType | null>(null);
  const navigate = useNavigate();
  const [openBookState, setOpenBook] = useState<string | null>(null);
  const [filesRemoveState, setFilesRemove] = useState<number[]>([]);
  const [filesRemoveStatusState, setFilesRemoveStatus] = useState<'inactive' | 'removing' | 'waiting'>('inactive');

  useEffect(() => {
    (async () => {
      try {
        const publishersResponse = await api.get<PublishersType>(endpoints.getPublishers(0, 1000));
        const schoolClassesResponse = await api.get<SchoolClassesType>(endpoints.getSchoolClasses());

        setSchoolClasses(schoolClassesResponse.data);
        setPublishers(publishersResponse.data.sort(sortPublishers));
      } catch (err) {}
    })();
  }, []);

  useEffect(() => {
    if (openBookState) {
      document.getElementsByTagName('body')[0].style.overflow = 'hidden';
      window.scrollTo(0, 0);
    } else document.getElementsByTagName('body')[0].style.overflow = 'auto';
  }, [openBookState]);

  const removeFilesFunc = (status: string) => {
    if (filesRemoveStatusState === 'removing' || (status === 'removing' && filesRemoveState.length > 0))
      api.delete(endpoints.getFile(filesRemoveState[0])).then((a) => {
        filesRemoveState.shift();
        setFilesRemoveStatus('waiting');
        if (a.status === 200) removeFilesFunc('removing');
      });
    else {
      message.success('Данные успешно обновлены').then(() => navigate(-1));
    }
  };

  const createNmsMeeting = () => {
    api
      .post(endpoints.postNms(), {
        ...nmsMeetingState,
        archive: nmsMeetingState.archive || false,
        access_provided_to_members: nmsMeetingState.access_provided_to_members || false,
        date: nmsMeetingState.date || moment().format('YYYY-MM-DD'),
      })
      .then(() => {
        message.success('Заседание успешно создано');
        navigate(-1);
      });
  };

  const updateNmsMeeting = () => {
    if (filesRemoveState.length > 0) {
      removeFilesFunc('removing');
    }

    if (id)
      api
        .put(endpoints.getNmsById(id), { ...changesState, nms_textbook_groups: nmsMeetingState.nms_textbook_groups })
        .then(() => {
          if (filesRemoveState.length === 0) message.success('Данные успешно обновление').then(() => navigate(-1));
        })
        .catch(() => message.error('Ошибка'));
  };

  const onChange: DatePickerProps['onChange'] = (date: Moment | null) =>
    setNmsMeeting({ ...nmsMeetingState, date: moment(date).format('YYYY-MM-DD') });

  useEffect(() => {
    if (editNmsMeeting) setChangesState(nmsMeetingState);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editNmsMeeting, nmsMeetingState]);

  useEffect(() => {
    if (
      currentNmsMeeting?.nms_textbook_groups ||
      currentNmsMeeting?.archive ||
      currentNmsMeeting?.access_provided_to_members
    )
      setNmsMeeting({
        ...nmsMeetingState,
        archive: currentNmsMeeting.archive,
        access_provided_to_members: currentNmsMeeting.access_provided_to_members,
        nms_textbook_groups: currentNmsMeeting.nms_textbook_groups,
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editNmsMeeting, currentNmsMeeting]);

  return (
    <PagesContainerUI>
      {editNmsMeeting ? <BlueLayout /> : <BlueLayout>Добавить заседание НМС</BlueLayout>}
      {openBookState && <PdfViewerComponent closePdfViewer={() => setOpenBook(null)} bookLink={openBookState} />}

      <FormBlock white noBorderRadius>
        <GridItem align="flex-end" gap="30px" justify="flex-start" direction="row" redstar>
          <GridItem width="300px" direction="column" justify="flex-start" gap="10px" align="flex-start">
            <div className="text">Дата заседания</div>
            <CustomDatePicker format="DD.MM.YYYY" value={moment(nmsMeetingState?.date)} onChange={onChange} />
          </GridItem>
          <CustomCheckbox
            checked={nmsMeetingState?.archive}
            onChange={(e: CheckboxChangeEvent) => setNmsMeeting({ ...nmsMeetingState, archive: e.target.checked })}
          >
            Архивное
          </CustomCheckbox>
          <CustomCheckbox
            checked={nmsMeetingState?.access_provided_to_members}
            onChange={(e: CheckboxChangeEvent) =>
              setNmsMeeting({ ...nmsMeetingState, access_provided_to_members: e.target.checked })
            }
          >
            Предоставлен доступ членам НМС
          </CustomCheckbox>
        </GridItem>

        <CustomLayout>Материалы заседания</CustomLayout>

        <Row gutter={[20, 20]}>
          {nmsMeetingState?.nms_textbook_groups &&
            nmsMeetingState.nms_textbook_groups.map((el, index) => {
              return (
                nmsMeetingState?.nms_textbook_groups &&
                !el?.delete && (
                  <NmsMeetingMaterial
                    publishers={publishersState}
                    changeMaterial={(material, id) => {
                      setNmsMeeting({
                        ...nmsMeetingState,
                        nms_textbook_groups: nmsMeetingState?.nms_textbook_groups?.map((e, idx) => {
                          return idx === id ? material : e;
                        }),
                      });
                    }}
                    mat_id={index}
                    setOpenBook={(value: string) => setOpenBook(value)}
                    school_classes={schoolClassesState}
                    addRemovableFile={(id) => setFilesRemove([...filesRemoveState, id])}
                    editing={editNmsMeeting}
                    // material={nmsMeetingState.nms_textbook_groups[index]}
                    material={nmsMeetingState?.nms_textbook_groups[index]}
                    removeMaterial={() =>
                      nmsMeetingState.nms_textbook_groups &&
                      setNmsMeeting({
                        ...nmsMeetingState,
                        nms_textbook_groups: nmsMeetingState.nms_textbook_groups.map((e, idx) => {
                          return idx === index ? { ...e, delete: true } : e;
                        }),
                      })
                    }
                    key={index}
                  />
                )
              );
            })}
        </Row>

        <CustomButton
          width="230px"
          lined
          onClick={() =>
            setNmsMeeting({
              ...nmsMeetingState,
              nms_textbook_groups: nmsMeetingState.nms_textbook_groups
                ? [...nmsMeetingState.nms_textbook_groups, { files: [] }]
                : [{ files: [] }],
            })
          }
        >
          Добавить учебник
        </CustomButton>

        <div className="to-center">
          {filesRemoveStatusState === 'waiting' ? (
            <Spin />
          ) : (
            <CustomButton onClick={() => (editNmsMeeting ? updateNmsMeeting() : createNmsMeeting())}>
              Сохранить
            </CustomButton>
          )}
        </div>
      </FormBlock>
    </PagesContainerUI>
  );
};
